'use client'

import { cn } from '@brew/ui/cn'
import { Children, type ComponentProps, type HTMLAttributes } from 'react'
import { FudePromptInputTextarea } from '@/components/ai-elements/prompt-input-fude-textarea'
import {
  InputGroupAddon,
  InputGroupButton,
  type InputGroupTextarea,
} from '@/components/ui/input-group'
import type { GetPromptSuggestionContext } from '@/hooks/use-prompt-suggestion-context'
import {
  type PromptInputMentionItem,
  useOptionalPromptInputController,
  usePromptInputAttachments,
} from './provider'

export type PromptInputBodyProps = HTMLAttributes<HTMLDivElement>

export const PromptInputBody = ({
  className,
  ...props
}: PromptInputBodyProps) => (
  <div className={cn('contents', className)} {...props} />
)

export type PromptInputTextareaProps = ComponentProps<
  typeof InputGroupTextarea
> & {
  shimmerPlaceholder?: boolean
  /**
   * When false, no inline autocomplete suggestions are fetched. Default
   * is true. Set to false on unauthenticated surfaces to keep the
   * `/api/prompt-autocomplete` endpoint from being invoked.
   */
  enableAutocomplete?: boolean
  /** Increment to focus the Fude editor and place its caret at the end. */
  focusRequestKey?: number
  /**
   * Identity-stable context getter for autocomplete requests — see
   * `usePromptSuggestionContext`.
   */
  getSuggestionContext?: GetPromptSuggestionContext
  onFetchMentions?:
    | ((query: string) => Promise<Array<PromptInputMentionItem>>)
    | ((query: string) => Array<PromptInputMentionItem>)
}

export const PromptInputTextarea = ({
  onChange,
  className,
  placeholder = 'What would you like to know?',
  shimmerPlaceholder = false,
  value,
  enableAutocomplete = true,
  focusRequestKey,
  getSuggestionContext,
  onFetchMentions,
  ...props
}: PromptInputTextareaProps) => {
  const controller = useOptionalPromptInputController()
  const attachments = usePromptInputAttachments()
  const { disabled: isDisabled, ...restProps } = props
  const dataTestId = (restProps as { 'data-testid'?: string })['data-testid']

  return (
    <FudePromptInputTextarea
      attachments={attachments}
      controller={controller?.textInput ?? null}
      className={className}
      disabled={isDisabled}
      enableAutocomplete={enableAutocomplete}
      focusRequestKey={focusRequestKey}
      getSuggestionContext={getSuggestionContext}
      onChange={onChange}
      onFetchMentions={onFetchMentions}
      placeholder={placeholder}
      shimmerPlaceholder={shimmerPlaceholder}
      value={value as string | undefined}
      data-testid={dataTestId}
    />
  )
}

export type PromptInputHeaderProps = Omit<
  ComponentProps<typeof InputGroupAddon>,
  'align'
>

export const PromptInputHeader = ({
  className,
  ...props
}: PromptInputHeaderProps) => (
  <InputGroupAddon
    align="block-end"
    className={cn('order-first flex-wrap gap-1 pb-0', className)}
    {...props}
  />
)

export type PromptInputFooterProps = Omit<
  ComponentProps<typeof InputGroupAddon>,
  'align'
>

export const PromptInputFooter = ({
  className,
  ...props
}: PromptInputFooterProps) => (
  <InputGroupAddon
    align="block-end"
    className={cn('justify-between gap-1 pb-2.5', className)}
    {...props}
  />
)

export type PromptInputToolsProps = HTMLAttributes<HTMLDivElement>

export const PromptInputTools = ({
  className,
  ...props
}: PromptInputToolsProps) => (
  <div className={cn('flex items-center gap-1', className)} {...props} />
)

export type PromptInputButtonProps = ComponentProps<typeof InputGroupButton>

export const PromptInputButton = ({
  variant = 'ghost',
  className,
  size,
  ...props
}: PromptInputButtonProps) => {
  const nextSize =
    size ?? (Children.count(props.children) > 1 ? 'sm' : 'icon-sm')

  return (
    <InputGroupButton
      className={cn(className)}
      size={nextSize}
      type="button"
      variant={variant}
      {...props}
    />
  )
}
